import { Button, TooltipProvider } from '@roger/ui-components';
import { RefreshCw } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  getAgencyPayerEnrollments,
  refreshAgencyEnrollments,
  type PayerEnrollmentGroup,
  type PayerEnrollmentsResponse,
} from '../../services/clinicsApi';
import { useStore } from '../../store';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { EnrollmentFilters } from './EnrollmentFilters';
import { PayerEnrollmentsTable } from './PayerEnrollmentsTable';
import { PayerDetailsDrawer } from './PayerDetailsDrawer';
import { PayerSteps } from './PayerSteps';
import { SupportIcon } from './SupportIcon';
import {
  PAYER_TRANSACTION_LABELS,
  agencyTitle,
  markLabel,
  countOutstanding,
  enrollmentTodos,
  linesInView,
  summaryRank,
  transactionStates,
  transactionsInView,
  type PayerTransaction,
} from './payerEnrollments';

interface EnrollmentsTabProps {
  canRefresh: boolean;
  /**
   * Report the outstanding count back to Admin, which owns the tab badge.
   *
   * Admin fetches it once on mount; without this the badge would keep that
   * first answer while somebody watches the table change under a Refresh.
   * Need not be referentially stable — it is held in a ref.
   */
  onCountChange?: (count: number) => void;
}

// The tab is visible to anyone with the feature flag and the Insurance
// permission, but the endpoint needs access to every clinic in the agency.
// Says that some clinics are out of reach, never which ones.
const NO_CLINIC_ACCESS_MESSAGE =
  'Enrollments covers every clinic in the agency, and you don’t have access to all of them.';

/**
 * Admin > Enrollments: one row per payer, the way Stedi's payer network table
 * lays it out, each opening to every clinic's enrollments with that payer.
 *
 * Built here, behind the flag, and swapped in for the Insurance tab once it
 * does everything that tab does.
 */
export function EnrollmentsTab({
  canRefresh,
  onCountChange,
}: EnrollmentsTabProps) {
  // Held in a ref, not read from the closure: `load` runs from an effect keyed
  // on its own identity, so a caller passing an inline arrow would otherwise
  // give it a new identity every render and refetch forever.
  const countCallback = useRef(onCountChange);
  useEffect(() => {
    countCallback.current = onCountChange;
  }, [onCountChange]);
  const { showError } = useStore(
    useShallow(state => ({ showError: state.showError }))
  );
  const [data, setData] = useState<PayerEnrollmentsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Kept separately from `data` because the two failures look different: a
  // first load leaves no rows at all, a failed refresh leaves the previous
  // ones on screen. Either way the table must not be the thing that answers,
  // because an empty table here reads as "nothing outstanding" — the exact
  // wrong answer to give wrongly on a tab whose job is chasing deadlines.
  const [loadFailed, setLoadFailed] = useState(false);
  // Separate from `loadFailed` because retrying is the wrong offer: the
  // endpoint is agency-wide and this caller cannot see every clinic, so the
  // answer will not change until somebody grants them the rest.
  const [missingClinicAccess, setMissingClinicAccess] = useState(false);

  const [search, setSearch] = useState('');
  const [clinicIds, setClinicIds] = useState<string[]>([]);
  const [selectedTransactions, setSelectedTransactions] = useState<
    PayerTransaction[]
  >([]);
  const transactions = useMemo(
    () => transactionsInView(selectedTransactions),
    [selectedTransactions]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const loaded = await getAgencyPayerEnrollments();
      setData(loaded);
      // The same statuses the count endpoint counts, from the rows already in
      // hand rather than asking again.
      countCallback.current?.(countOutstanding(loaded.payers ?? []));
      setLoadFailed(false);
    } catch (error) {
      console.error('Failed to load enrollments', error);
      if (getHttpStatus(error) === 403) {
        showError(NO_CLINIC_ACCESS_MESSAGE);
        setMissingClinicAccess(true);
      } else {
        showError('Unable to load enrollments. Please try again.');
      }
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const result = await refreshAgencyEnrollments();
      await load();
      if (result.clinics_failed > 0) {
        // The sync takes a savepoint per clinic, so some can fail while the
        // rest are written and the call still answers 200. A clearinghouse
        // outage is a 502 and lands in the catch below instead.
        showError(
          `Could not refresh ${result.clinics_failed} of ${
            result.clinics_failed + result.clinics_refreshed
          } clinics. Please try again.`
        );
      }
    } catch (error) {
      console.error('Failed to refresh enrollments', error);
      if (getHttpStatus(error) === 403) {
        showError(NO_CLINIC_ACCESS_MESSAGE);
        setMissingClinicAccess(true);
      } else {
        showError('Unable to refresh enrollments. Please try again.');
      }
      setLoadFailed(true);
    } finally {
      setIsRefreshing(false);
    }
  };

  const payers = data?.payers ?? [];
  const noPayerInsurances = data?.no_payer_insurances ?? [];
  const clinics = data?.clinics ?? [];

  // One line per kind of gap, with the clinics listed after it. Six clinics
  // with an unverifiable NPI were six near-identical sentences in one long
  // run, and the repetition hid how few distinct problems there are.
  const blindSpots = useMemo(() => {
    const parts: string[] = [];
    const withoutNpi = data?.clinics_without_npi ?? [];
    if (withoutNpi.length > 0) {
      parts.push(`No NPI: ${withoutNpi.join(', ')}`);
    }
    const unverifiable = data?.clinics_with_unverifiable_npi ?? [];
    if (unverifiable.length > 0) {
      // Deliberately vague about the cause. The agency cannot act on this and
      // the cause involves another tenant, so it says the table is incomplete
      // and nothing more.
      parts.push(`NPI couldn't be verified: ${unverifiable.join(', ')}`);
    }
    return parts;
  }, [data]);

  // Search narrows payers; the clinic filter narrows the clinic lines inside
  // them and drops payers no clinic in view deals with. Then by summary: Needs action
  // first, then Not submitted, Submitted, Live.
  const visiblePayers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return payers
      .filter(
        payer =>
          clinicIds.length === 0 ||
          (payer.clinics ?? []).some(line =>
            clinicIds.includes(line.agency_clinic_id)
          )
      )
      .filter(
        payer =>
          !needle ||
          [
            payer.name,
            payer.primary_payer_id,
            ...(payer.other_names ?? []),
            // The agency's own insurance names, which lead each row.
            ...linesInView(payer, clinicIds).flatMap(line =>
              (line.insurances ?? []).map(insurance => insurance.name)
            ),
          ]
            .filter(Boolean)
            .some(field => field?.toLowerCase().includes(needle))
      )
      .map(payer => ({
        payer,
        rank: summaryRank(payer, clinicIds, transactions),
      }))
      .sort((a, b) =>
        a.rank === b.rank
          ? a.payer.name.localeCompare(b.payer.name)
          : a.rank - b.rank
      )
      .map(({ payer }) => payer);
  }, [payers, search, clinicIds, transactions]);

  // Not narrowed by the payer search: a shared job covers other payers too,
  // and ticking it off should tick all of them.
  const todos = useMemo(
    () => enrollmentTodos(payers, clinicIds, transactions),
    [payers, clinicIds, transactions]
  );

  // The payer shown beside the table. Its jobs are read from the latest load,
  // so a step marked done leaves the panel on the reload.
  const [stepsFor, setStepsFor] = useState<string | null>(null);
  // Only a payer the table still shows: a filter that hides it closes the pane
  // rather than leave it describing a payer out of view.
  const openPayer = visiblePayers.find(payer => payer.key === stepsFor) ?? null;
  const openTodos = todos.filter(todo =>
    todo.steps.some(step => step.payerKey === stepsFor)
  );

  // Pinned against the page's own scroll area, not the window: the app's top
  // bar sits outside it and its padding would push the pane down. So the pane
  // is placed and sized from that area, measured, with a small margin.
  const paneRef = useRef<HTMLDivElement>(null);
  // Scrolls on its own only once pinned: before that its bottom is off the
  // screen, and a scroll over it should move the page, not the pane.
  const [isPinned, setIsPinned] = useState(false);
  const [pinned, setPinned] = useState<{
    top: number;
    maxHeight: number;
  } | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      let area = paneRef.current?.parentElement ?? null;
      while (area && !/(auto|scroll)/.test(getComputedStyle(area).overflowY)) {
        area = area.parentElement;
      }
      if (!window.matchMedia('(min-width: 1024px)').matches) {
        setPinned(null);
        return;
      }
      const margin = 16;
      // No scroll area of its own means the window scrolls.
      const padding = area
        ? parseFloat(getComputedStyle(area).paddingTop) || 0
        : 0;
      setPinned({
        top: margin - padding,
        maxHeight: (area?.clientHeight ?? window.innerHeight) - margin * 2,
      });
    };
    let area = paneRef.current?.parentElement ?? null;
    while (area && !/(auto|scroll)/.test(getComputedStyle(area).overflowY)) {
      area = area.parentElement;
    }
    const scroller: HTMLElement | Window = area ?? window;
    const checkPinned = () => {
      const pane = paneRef.current;
      if (!pane) return;
      const areaTop = area ? area.getBoundingClientRect().top : 0;
      // Or the page can go no further, so the pane is all there is to scroll.
      const atEnd = area
        ? area.scrollTop + area.clientHeight >= area.scrollHeight - 1
        : window.scrollY + window.innerHeight >=
          document.documentElement.scrollHeight - 1;
      setIsPinned(pane.getBoundingClientRect().top <= areaTop + 17 || atEnd);
    };
    measure();
    checkPinned();
    window.addEventListener('resize', measure);
    scroller.addEventListener('scroll', checkPinned, { passive: true });
    return () => {
      window.removeEventListener('resize', measure);
      scroller.removeEventListener('scroll', checkPinned);
    };
  }, [data, stepsFor]);

  const openTitle = openPayer ? agencyTitle(openPayer, clinicIds) : null;
  const openStatus = useMemo(() => {
    if (!openPayer) return [];
    const states = new Map(
      transactionStates(openPayer, clinicIds, transactions).map(item => [
        item.transaction,
        item.state,
      ])
    );
    return transactions.map(transaction => {
      const support = openPayer[transaction]?.support;
      const state = states.get(transaction) ?? null;
      return { transaction, support, state, label: markLabel(support, state) };
    });
  }, [openPayer, clinicIds, transactions]);
  const [detailsFor, setDetailsFor] = useState<PayerEnrollmentGroup | null>(
    null
  );

  const visibleNoPayer = useMemo(
    () =>
      noPayerInsurances.filter(
        insurance =>
          (clinicIds.length === 0 ||
            (insurance.agency_clinic_id &&
              clinicIds.includes(insurance.agency_clinic_id))) &&
          (!search.trim() ||
            insurance.name.toLowerCase().includes(search.trim().toLowerCase()))
      ),
    [noPayerInsurances, search, clinicIds]
  );

  return (
    <div className='space-y-4'>
      {blindSpots.length > 0 && (
        <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
          <p>Some enrollment status can&apos;t be checked:</p>
          <ul className='mt-1 list-disc space-y-0.5 pl-5'>
            {blindSpots.map(part => (
              <li key={part}>{part}</li>
            ))}
          </ul>
        </div>
      )}

      <EnrollmentFilters
        search={search}
        onSearchChange={setSearch}
        clinics={clinics}
        selectedClinicIds={clinicIds}
        onClinicIdsChange={setClinicIds}
        selectedTransactions={selectedTransactions}
        onTransactionsChange={setSelectedTransactions}
        actions={
          canRefresh &&
          !missingClinicAccess && (
            <Button
              onClick={handleRefresh}
              disabled={isRefreshing}
              variant='outline'
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
          )
        }
      />

      {isLoading && !data ? (
        <p className='text-sm text-muted-foreground'>Loading enrollments…</p>
      ) : missingClinicAccess ? (
        <div className='rounded-md border py-8 text-center'>
          <p className='text-sm text-muted-foreground'>
            {NO_CLINIC_ACCESS_MESSAGE}
          </p>
        </div>
      ) : loadFailed ? (
        <div className='space-y-3 rounded-md border py-8 text-center'>
          <p className='text-sm text-muted-foreground'>
            {data
              ? 'Couldn’t check for changes, so this may be out of date.'
              : 'Couldn’t load enrollments.'}
          </p>
          <Button onClick={() => void load()} disabled={isLoading} size='sm'>
            Try again
          </Button>
        </div>
      ) : (
        // Half and half: the table to pick from, the payer picked beside it.
        // Stacked below lg, with the payer first once one is picked.
        <div className='flex flex-col gap-4 lg:flex-row lg:items-start'>
          <div className='min-w-0 lg:w-1/2'>
            <PayerEnrollmentsTable
              payers={visiblePayers}
              noPayerInsurances={visibleNoPayer}
              clinicIds={clinicIds}
              transactions={transactions}
              selectedKey={openPayer?.key ?? null}
              onSelect={key => {
                setStepsFor(key);
                // Stacked, the pane is above the table: bring it into view.
                if (!pinned) {
                  requestAnimationFrame(() =>
                    paneRef.current?.scrollIntoView({ block: 'start' })
                  );
                }
              }}
            />
          </div>
          <div
            ref={paneRef}
            style={
              pinned
                ? { ...pinned, overflowY: isPinned ? 'auto' : 'hidden' }
                : undefined
            }
            className={`min-w-0 lg:sticky lg:w-1/2 ${
              openPayer
                ? 'order-first rounded-md border lg:order-none'
                : 'hidden lg:block'
            }`}
          >
            {openPayer ? (
              <div className='p-4'>
                <div className='flex items-start gap-2'>
                  <div className='min-w-0 flex-1'>
                    {/* The name ends in "…"; the count stays whole. */}
                    <h2
                      className='flex text-lg font-semibold'
                      title={openTitle?.all.join(', ')}
                    >
                      <span className='truncate'>
                        {openTitle?.all[0] ?? openPayer.name}
                      </span>
                      {openTitle && openTitle.all.length > 1 && (
                        <span className='shrink-0 whitespace-nowrap'>
                          &nbsp;+{openTitle.all.length - 1} more
                        </span>
                      )}
                    </h2>
                    {openTodos.length === 0 && (
                      <p className='text-sm text-muted-foreground'>
                        {openStatus.every(item => item.label.startsWith('Live'))
                          ? 'All set. Nothing for your agency to do.'
                          : 'Nothing for your agency to do.'}
                      </p>
                    )}
                  </div>
                </div>
                {openTodos.length === 0 && (
                  // Nothing to do, so where it stands, in words: what the
                  // row's marks say without hovering each one.
                  <div className='mt-4 space-y-3 text-sm'>
                    {/* The marks carry tooltips, which need a provider here too. */}
                    <TooltipProvider delayDuration={200}>
                      <ul className='divide-y border-y'>
                        {openStatus.map(
                          ({ transaction, label, support, state }) => (
                            <li
                              key={transaction}
                              className='flex items-center gap-3 py-2'
                            >
                              <SupportIcon support={support} state={state} />
                              <span className='w-24 shrink-0 font-medium'>
                                {PAYER_TRANSACTION_LABELS[transaction]}
                              </span>
                              <span className='text-muted-foreground'>
                                {label}
                              </span>
                            </li>
                          )
                        )}
                      </ul>
                    </TooltipProvider>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => setDetailsFor(openPayer)}
                    >
                      Payer details
                    </Button>
                  </div>
                )}
                {openTodos.length > 0 && (
                  <div className='mt-4'>
                    <PayerSteps
                      payerKey={openPayer.key}
                      todos={openTodos}
                      onChanged={() => void load()}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className='rounded-md border border-dashed py-12 text-center text-sm text-muted-foreground'>
                Select a payer to see enrollment instructions.
              </div>
            )}
          </div>
        </div>
      )}
      <PayerDetailsDrawer
        payer={detailsFor}
        clinicIds={clinicIds}
        onClose={() => setDetailsFor(null)}
      />
    </div>
  );
}
